'use client';

import { useCallback, useEffect, useEffectEvent, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useTranslations } from 'use-intl';

import {
  useProjectResponseModeContext,
  type ProjectResponseModeEvent,
} from '@/shared-survey';
import { QuestionType } from '@/survey-graphql';
import type { GetProjectQuery } from '@/survey-graphql';
import { toast } from '@/ui';

import type { AiFormsResponseValues } from './survey-ai-forms-form.hooks';
import { isMatrixAnswer } from './survey-ai-forms-matrix.types';
import { useVoiceHandling } from './survey-ai-forms-voice.hooks';
import {
  getEmptySkipValueForQuestion,
  getTranscriptToPersistOnVoiceCancel,
  isMatrixComplete,
  isMatrixQuestion,
  removeLastOccurenceAndEverythingAfter,
  shouldClearTranscriptOnTextMode,
} from './survey-ai-forms.helpers';
import type { FormAnswerValue, ResponseMode } from './survey-ai-forms.types';

type ProjectQuestion = NonNullable<
  GetProjectQuery['project']
>['questions'][number];

interface UseAiSectionProps {
  currentQuestion: ProjectQuestion | null;
  questions: ProjectQuestion[];
  onResponseSubmit: (value: FormAnswerValue) => void;
  saveTranscript: (questionId: string, transcript: string) => void;
  getTranscript: (questionId: string) => string;
  responseMode: ResponseMode;
}

/**
 * Hook for managing AI section state and business logic
 */
export function useAiSection({
  currentQuestion,
  questions,
  onResponseSubmit,
  saveTranscript,
  getTranscript,
  responseMode,
}: UseAiSectionProps) {
  const t = useTranslations('AIForms.toast');

  const {
    control,
    getValues,
    setValue: setFormValue,
  } = useFormContext<AiFormsResponseValues>();

  const {
    eventsObservable,
    currentTranscript,
    setCurrentTranscript,
    manuallyEditTranscript,
  } = useProjectResponseModeContext();

  // Create a wrapper for setValue that matches the expected signature
  const setValue = useCallback(
    (
      questionId: string,
      value: FormAnswerValue,
      options?: {
        shouldDirty?: boolean;
        shouldTouch?: boolean;
        shouldValidate?: boolean;
      }
    ) => {
      setFormValue(`answers.${questionId}`, value, options);
    },
    [setFormValue]
  );

  const currentQuestionIdRef = useRef<string | null>(
    currentQuestion?.id ?? null
  );
  const previousQuestionIdRef = useRef<string | null>(null);
  const pendingClearRef = useRef(false);

  const onFormattingSettled = useCallback(() => {
    if (!pendingClearRef.current) return;
    const questionId = currentQuestionIdRef.current;
    pendingClearRef.current = false;
    if (questionId) {
      setCurrentTranscript('');
      saveTranscript(questionId, '');
    }
  }, [saveTranscript, setCurrentTranscript]);

  const { isFormattingTranscript, processTranscript } = useVoiceHandling({
    setValue,
    onFormattingSettled,
  });

  const isRequiredMatrixIncomplete = useCallback(
    (question: ProjectQuestion, answer: FormAnswerValue): boolean => {
      if (!isMatrixQuestion(question) || question.required !== true) {
        return false;
      }
      const matrix = isMatrixAnswer(answer) ? answer : undefined;
      if (isMatrixComplete(question, matrix)) return false;
      const hasAnyAnswer = matrix && Object.keys(matrix).length > 0;
      if (hasAnyAnswer) return false;
      toast.add({
        title: t('matrixIncomplete'),
        type: 'error',
      });
      return true;
    },
    [t]
  );

  const allAnswers = useWatch({ control, name: 'answers' });

  const onQuestionChanged = useEffectEvent(
    (
      event: Extract<ProjectResponseModeEvent, { type: 'question_changed' }>
    ) => {
      const questionId = event.questionId;
      currentQuestionIdRef.current = questionId;

      if (!questionId) return;
      if (previousQuestionIdRef.current === questionId) return;

      const savedTranscript = getTranscript(questionId) || '';

      setCurrentTranscript(savedTranscript);

      const targetQuestion = questions.find((q) => q.id === questionId);
      if (targetQuestion?.type === QuestionType.OpenEnded) {
        const currentFormValue = getValues(`answers.${questionId}`);
        const hasExistingFormValue =
          typeof currentFormValue === 'string' &&
          currentFormValue.trim().length > 0;

        if (!hasExistingFormValue && savedTranscript.trim()) {
          setValue(questionId, savedTranscript, {
            shouldValidate: false,
            shouldDirty: false,
          });
        }
      }

      previousQuestionIdRef.current = questionId;
    }
  );

  const onResponseModeChanged = useEffectEvent(
    (
      event: Extract<
        ProjectResponseModeEvent,
        { type: 'response_mode_changed' }
      >
    ) => {
      if (event.next === 'voice') {
        pendingClearRef.current = false;
      }

      const question = currentQuestion;

      const transcriptToPersist = getTranscriptToPersistOnVoiceCancel({
        previousResponseMode: event.prev,
        responseMode: event.next,
        questionType: question?.type,
        capturedTranscript: event.preChangeTranscript,
      });

      if (transcriptToPersist !== null && question) {
        setValue(question.id, transcriptToPersist, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        saveTranscript(question.id, transcriptToPersist);
      }

      if (
        question &&
        shouldClearTranscriptOnTextMode({
          nextMode: event.next,
          questionType: question.type,
          transcript: event.preChangeTranscript,
        })
      ) {
        if (isFormattingTranscript) {
          pendingClearRef.current = true;
        } else {
          setCurrentTranscript('');
          saveTranscript(question.id, '');
        }
      }
    }
  );

  const onTranscriptChanged = useEffectEvent(
    (
      event: Extract<ProjectResponseModeEvent, { type: 'transcript_changed' }>
    ) => {
      const questionId = currentQuestionIdRef.current;
      if (questionId) {
        saveTranscript(questionId, event.transcript);
      }
    }
  );

  const onProviderEvent = useEffectEvent((event: ProjectResponseModeEvent) => {
    switch (event.type) {
      case 'question_changed':
        onQuestionChanged(event);
        break;
      case 'response_mode_changed':
        onResponseModeChanged(event);
        break;
      case 'transcript_changed':
        onTranscriptChanged(event);
        break;
    }
  });

  useEffect(() => {
    const subscription = eventsObservable.subscribe(onProviderEvent);
    return () => subscription.unsubscribe();
  }, [eventsObservable]);

  useEffect(() => {
    onQuestionChanged({
      type: 'question_changed',
      questionId: currentQuestionIdRef.current,
    });
  }, []);

  // Get current question's value from form
  // Use allAnswers that was already declared at the top level
  const currentValue = currentQuestion
    ? allAnswers?.[currentQuestion.id]
    : undefined;

  const openEndedDisplayValue = (() => {
    if (currentQuestion?.type !== QuestionType.OpenEnded) {
      return currentTranscript;
    }

    const valueString = typeof currentValue === 'string' ? currentValue : '';
    return valueString || currentTranscript;
  })();

  const isMatrix = currentQuestion ? isMatrixQuestion(currentQuestion) : false;

  const hasValue = (() => {
    if (!currentQuestion || isFormattingTranscript) return false;

    const isVoiceMode = responseMode === 'voice';

    if (currentQuestion.type === QuestionType.OpenEnded) {
      const formStr = typeof currentValue === 'string' ? currentValue : '';
      if (formStr.trim().length > 0) return true;
      if (isVoiceMode && currentTranscript.trim().length > 0) return true;
      return false;
    }

    if (isMatrix) {
      const hasMatrixValue =
        currentValue !== undefined &&
        currentValue !== null &&
        typeof currentValue === 'object' &&
        !Array.isArray(currentValue) &&
        Object.keys(currentValue).length > 0;

      if (isVoiceMode) {
        return hasMatrixValue || currentTranscript.trim().length > 0;
      }
      return hasMatrixValue;
    }

    if (isVoiceMode) {
      const hasTranscript = currentTranscript.trim().length > 0;
      const hasUIValue =
        currentValue !== undefined &&
        currentValue !== '' &&
        (Array.isArray(currentValue) ? currentValue.length > 0 : true);

      return hasTranscript || hasUIValue;
    }

    return (
      currentValue !== undefined &&
      currentValue !== '' &&
      (Array.isArray(currentValue) ? currentValue.length > 0 : true)
    );
  })();

  // Handle value change from question UI (rating slider, choice buttons, etc.)
  const handleQuestionValueChange = (value: FormAnswerValue) => {
    if (currentQuestion) {
      setValue(currentQuestion.id, value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  };

  // Handle transcript changes (for non-open-ended questions in voice mode)
  // When user manually edits the transcript, notify the voice system
  const handleTranscriptChange = (value: string) => {
    // Use manuallyEditTranscript to notify voice system of manual edit (like chat does)
    manuallyEditTranscript(value);
    // Save transcript for submission
    if (currentQuestion) {
      saveTranscript(currentQuestion.id, value);
    }
  };

  // Handle open-ended text input changes (when user types directly)
  const handleOpenEndedChange = (value: string) => {
    if (currentQuestion) {
      // Update form value immediately
      setValue(currentQuestion.id, value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      // CRITICAL: Only update the voice transcript if the actual content changed.
      // If the user only added a space, we don't want to notify the transcript system
      // because that can trigger a concatenation loop in the STT worker/provider.
      // The openEndedDisplayValue logic above will ensure the space is visible.
      const currentTrimmed = (
        typeof currentValue === 'string' ? currentValue : ''
      ).trim();
      const nextTrimmed = value.trim();

      if (nextTrimmed !== currentTrimmed) {
        // Also update transcript value to keep them in sync
        // Use manuallyEditTranscript to notify voice system of manual edit (like chat does)
        manuallyEditTranscript(value);
        // Save transcript for submission
        saveTranscript(currentQuestion.id, value);
      }

      // If value is empty, also clear any saved transcript
      if (!value.trim()) {
        saveTranscript(currentQuestion.id, '');
      }
    }
  };

  // Handle submit - behavior depends on question type
  const handleSubmit = async () => {
    if (!currentQuestion) return;

    const isVoiceMode = responseMode === 'voice';

    // For open-ended questions, use transcript value
    if (currentQuestion.type === QuestionType.OpenEnded) {
      if (currentTranscript.trim()) {
        // Update form with transcript
        setValue(currentQuestion.id, currentTranscript, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        // Save transcript for submission
        saveTranscript(currentQuestion.id, currentTranscript);
        onResponseSubmit(currentTranscript);
      } else if (
        currentQuestion.required !== true &&
        !openEndedDisplayValue.trim() &&
        !currentTranscript.trim()
      ) {
        const skipValue = getEmptySkipValueForQuestion(currentQuestion);
        setValue(currentQuestion.id, skipValue, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        saveTranscript(currentQuestion.id, '');
        onResponseSubmit(skipValue);
      }
    } else {
      // For other question types: check mode
      if (isVoiceMode) {
        // VOICE MODE: if transcript exists, format it and update the question
        if (currentTranscript.trim()) {
          // Save transcript BEFORE formatting to ensure it's available when onResponseSubmit is called
          saveTranscript(currentQuestion.id, currentTranscript);

          const formattedAnswer = await processTranscript(
            currentQuestion,
            currentTranscript,
            currentValue
          );

          if (formattedAnswer) {
            // Use the formatted answer directly (it's already in the correct format)
            setValue(currentQuestion.id, formattedAnswer, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });

            if (isRequiredMatrixIncomplete(currentQuestion, formattedAnswer)) {
              return;
            }

            onResponseSubmit(formattedAnswer);
          }
        } else if (hasValue && currentValue !== undefined) {
          if (isRequiredMatrixIncomplete(currentQuestion, currentValue)) {
            return;
          }
          // If no transcript but has value from UI, use that
          onResponseSubmit(currentValue);
        } else if (currentQuestion.required !== true) {
          const skipValue = getEmptySkipValueForQuestion(currentQuestion);
          setValue(currentQuestion.id, skipValue, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          onResponseSubmit(skipValue);
        }
      } else {
        // NON-VOICE MODE: just use the value directly, no formatTranscriptAnswer
        if (hasValue && currentValue !== undefined) {
          if (isRequiredMatrixIncomplete(currentQuestion, currentValue)) {
            return;
          }
          // Don't save transcript in non-voice mode for non-open-ended questions
          onResponseSubmit(currentValue);
        } else if (currentQuestion.required !== true) {
          const skipValue = getEmptySkipValueForQuestion(currentQuestion);
          setValue(currentQuestion.id, skipValue, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          onResponseSubmit(skipValue);
        }
      }
    }
  };

  // Handle Next button - for all question types in AI section
  const handleAiNext = async () => {
    if (!currentQuestion) return;

    const isVoiceMode = responseMode === 'voice';

    if (currentQuestion.type === QuestionType.OpenEnded) {
      // For open-ended: use the display value (form value or transcript)
      const valueToSave = openEndedDisplayValue.trim();
      if (valueToSave) {
        // Ensure form value is set
        setValue(currentQuestion.id, valueToSave, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        // Save transcript separately for submission if it came from voice
        if (currentTranscript.trim()) {
          saveTranscript(currentQuestion.id, currentTranscript);
        }
        // Call onResponseSubmit to handle form message and navigation
        onResponseSubmit?.(valueToSave);
      } else if (currentQuestion.required !== true) {
        const skipValue = getEmptySkipValueForQuestion(currentQuestion);
        setValue(currentQuestion.id, skipValue, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        saveTranscript(currentQuestion.id, '');
        onResponseSubmit?.(skipValue);
      }
    } else {
      // For other question types: check mode
      if (isVoiceMode) {
        // VOICE MODE: prioritize transcript if it exists
        if (currentTranscript.trim()) {
          // Save transcript BEFORE formatting
          saveTranscript(currentQuestion.id, currentTranscript);

          // Format the transcript and update the question
          const formattedAnswer = await processTranscript(
            currentQuestion,
            currentTranscript,
            currentValue
          );

          if (formattedAnswer) {
            // Use the formatted answer directly (it's already in the correct format)
            setValue(currentQuestion.id, formattedAnswer, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });

            if (isRequiredMatrixIncomplete(currentQuestion, formattedAnswer)) {
              return;
            }

            // Call onResponseSubmit to handle form message and navigation
            onResponseSubmit?.(formattedAnswer);
          }
        } else if (hasValue && currentValue !== undefined) {
          if (isRequiredMatrixIncomplete(currentQuestion, currentValue)) {
            return;
          }
          // No transcript but has UI value, use that
          onResponseSubmit?.(currentValue);
        } else if (currentQuestion.required !== true) {
          const skipValue = getEmptySkipValueForQuestion(currentQuestion);
          setValue(currentQuestion.id, skipValue, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          onResponseSubmit?.(skipValue);
        }
      } else {
        // NON-VOICE MODE: just use the current value directly, no formatTranscriptAnswer
        if (hasValue && currentValue !== undefined) {
          if (isRequiredMatrixIncomplete(currentQuestion, currentValue)) {
            return;
          }
          // Don't save transcript in non-voice mode for non-open-ended questions
          onResponseSubmit?.(currentValue);
        } else if (currentQuestion.required !== true) {
          const skipValue = getEmptySkipValueForQuestion(currentQuestion);
          setValue(currentQuestion.id, skipValue, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          onResponseSubmit?.(skipValue);
        }
      }
    }
  };

  // Handle voice command "next" or "next question"
  const handleVoiceNextCommand = useCallback(
    async (triggerWord: string) => {
      if (!currentQuestion) return;

      const submitOptionalSkip = () => {
        if (currentQuestion.required === true) {
          return;
        }
        const skipValue = getEmptySkipValueForQuestion(currentQuestion);
        setValue(currentQuestion.id, skipValue, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        saveTranscript(currentQuestion.id, '');
        onResponseSubmit?.(skipValue);
      };

      if (!currentTranscript?.trim()) {
        submitOptionalSkip();
        return;
      }

      // Remove trigger word from transcript before processing
      const cleanedTranscript = removeLastOccurenceAndEverythingAfter(
        currentTranscript,
        triggerWord
      );

      if (!cleanedTranscript.trim()) {
        submitOptionalSkip();
        return;
      }

      // Save the cleaned transcript to the form (without "next question")
      if (currentQuestion) {
        saveTranscript(currentQuestion.id, cleanedTranscript);
      }

      // Keep the original transcript visible in UI (user sees what they said)
      // Only the saved transcript is cleaned for submission

      const isVoiceMode = responseMode === 'voice';

      if (currentQuestion.type === QuestionType.OpenEnded) {
        // For open-ended: use cleaned transcript directly
        // formatTranscriptAndCreateAudioAsset will format it later (like chat does)
        setValue(currentQuestion.id, cleanedTranscript, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        onResponseSubmit?.(cleanedTranscript);
      } else {
        // For other question types in voice mode
        if (isVoiceMode) {
          // Format the cleaned transcript
          const formattedAnswer = await processTranscript(
            currentQuestion,
            cleanedTranscript,
            currentValue
          );

          if (formattedAnswer) {
            setValue(currentQuestion.id, formattedAnswer, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
            if (isRequiredMatrixIncomplete(currentQuestion, formattedAnswer)) {
              return;
            }
            onResponseSubmit?.(formattedAnswer);
          }
        } else if (hasValue && currentValue !== undefined) {
          if (isRequiredMatrixIncomplete(currentQuestion, currentValue)) {
            return;
          }
          // Non-voice mode with UI value
          onResponseSubmit?.(currentValue);
        }
      }
    },
    [
      currentQuestion,
      currentTranscript,
      responseMode,
      hasValue,
      currentValue,
      setValue,
      onResponseSubmit,
      processTranscript,
      saveTranscript,
      isRequiredMatrixIncomplete,
    ]
  );

  // Voice command: Listen for "Next" or "Next question"
  useEffect(() => {
    const subscription = eventsObservable.subscribe((event) => {
      if (event?.type === 'next_question' && event?.triggerWord) {
        void handleVoiceNextCommand(event.triggerWord);
      }
    });
    return () => subscription.unsubscribe();
  }, [eventsObservable, handleVoiceNextCommand]);

  return {
    // State
    transcriptValue: currentTranscript,
    setTranscriptValue: handleTranscriptChange,
    currentValue,
    openEndedDisplayValue,
    hasValue,
    isFormattingTranscript,

    // Handlers
    handleQuestionValueChange,
    handleOpenEndedChange,
    handleSubmit,
    handleAiNext,
  };
}
